import React, { useState, useEffect, useCallback, useContext } from 'react';
import './Login.css';
import LockIcon from '@mui/icons-material/Lock';
import { Logo } from '../../components/Logo/';
import { ThemeContext } from '../../lib/contexts/ThemeContext';
import {
  Button,
  Grid,
  Typography,
  TextField,
  FormControl,
  LinearProgress,
  Paper,
  Checkbox,
  FormGroup,
  AccordionSummary,
  Accordion,
  FormControlLabel,
  createTheme,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from '@mui/material';
import { PartnerContext, UserContext } from '../../lib/contexts';
import { ageFromDate, getCookie, prettyDate, setCookie } from '../../lib/helpers/helpers';
import { KeyboardArrowDown, KeyboardArrowLeft, KeyboardArrowUp, Login } from '@mui/icons-material';
import MarkdownRenderer from '../../components/MarkdownRenderer';
import { postUniversalLogin } from '../../lib/api';
import { useLoginStyles } from './LoginStyles';
import { getBetaClientHost } from '../../lib/constants/constants';
import { Launch } from '@material-ui/icons';

const LoginScreenUniversal = (props) => {
  const {
    state: { finalTheme },
  } = useContext(ThemeContext);
  const tempTheme = createTheme({
    palette: {
      primary: { main: '#EC5532' },
      secondary: { main: '#3D9E82' },
      tertiary: { main: '#1A1442' },
    },
  });
  const classes = useLoginStyles({ theme: tempTheme });
  const {
    actions: { setUser },
  } = useContext(UserContext);
  const {
    state: { welcome_message, id = 1 },
  } = useContext(PartnerContext);
  const [status, setStatus] = useState(null);
  const [emailBad, setEmailBad] = useState(false);
  const [tryingLogin, setTryingLogin] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [OTPLogin, setOTPLogin] = useState(false);
  const [accessCodeLogin, setAccessCodeLogin] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [OTP, setOTP] = useState('');
  const [helperText, setHelperText] = useState('');
  const isDarkMode = localStorage.getItem('THEME') === 'true';

  // New states for partner/user selection
  const [multiplePartners, setMultiplePartners] = useState(null);
  const [multipleUsers, setMultipleUsers] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    const phoneFromCookie = getCookie('phone');
    if (phoneFromCookie) {
      setPhone(phoneFromCookie);
    }
    const emailFromCookie = getCookie('email');
    if (!phoneFromCookie && emailFromCookie) {
      setLoginEmail(emailFromCookie);
    }
  }, []);

  const validateEmail = useCallback((email) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }, []);

  const redirectToCorrectApp = useCallback(
    (authKey, cookies = null) => {
      if (!authKey) {
        console.error('No auth provided');
        return;
      }

      const payload = JSON.stringify({ auth: authKey, cookies });
      const hashedKey = btoa(unescape(encodeURIComponent(payload)));
      const newAppUrl = `${getBetaClientHost(selectedPartner.partner_code)}/auth?auto_auth=${encodeURIComponent(
        hashedKey,
      )}`;
      window.location.href = newAppUrl;
    },
    [selectedPartner],
  );

  const loginRequest = useCallback(async () => {
    setStatus(null);
    setTryingLogin(true);

    const loginData = {
      user: {
        ...(!(accessCodeLogin || phone) && { email: loginEmail }),
        ...(!(accessCodeLogin || loginEmail) && { phone }),
        ...(accessCodeLogin && { access_code: accessCode }),
        ...(!accessCodeLogin && OTP ? { otp: OTP } : {}),
        ...(selectedPartner ? selectedPartner : {}),
        ...(selectedUser ? { id: selectedUser } : {}),
      },
    };

    try {
      const response = await postUniversalLogin(loginData, id);
      console.log('API response:', response.data);

      if (response.status === 200) {
        const data = response.data;

        // Handle multiple partners/users scenario
        if (data.multiple_partners) {
          setMultiplePartners(data.multiple_partners);
          setTryingLogin(false);
          return;
        }
        if (data.multiple_users) {
          setMultipleUsers(data.multiple_users);
          setTryingLogin(false);
          return;
        }

        // Check if the response indicates OTP was sent
        if (data.message === 'OTP sent for verification') {
          setOTPLogin(true);
          setHelperText(`OTP sent to ${data.channel}`);
          setTryingLogin(false);
          return;
        }

        // Handle final login success
        if (selectedPartner && data.user) {
          if (rememberMe) {
            if (loginEmail) setCookie('email', loginEmail, 365);
            if (phone) setCookie('phone', phone, 365);
          }
          redirectToCorrectApp({
            user: data.user,
            user_id: data.user.id,
            partner_id: selectedPartner.partner_id || data.user.partner_id,
            nav_user_id: data.nav_user_id,
            auth_token: data.auth_token,
          });
        }
      } else {
        setStatus('failure');
      }
    } catch (error) {
      console.error(error);
      setStatus('failure');
    }
    setTryingLogin(false);
  }, [loginEmail, phone, OTP, accessCode, accessCodeLogin, selectedPartner, selectedUser, id, rememberMe, setUser]);

  // Auto-trigger OTP login when OTP length is sufficient.
  useEffect(() => {
    if (OTPLogin && OTP?.length > 5) {
      loginRequest();
    }
  }, [OTPLogin, OTP, loginRequest]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter') {
        loginRequest();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [loginRequest]);

  // Function to reattempt login with selected partner
  const handleSelectPartner = (partnerObj) => {
    setSelectedPartner(partnerObj);
    setMultiplePartners(null);
    // Re-call loginRequest with partner_id included
    loginRequest();
  };

  // Function to reattempt login with selected user
  const handleSelectUser = (userId) => {
    setSelectedUser(userId);
    setMultipleUsers(null);
    loginRequest();
  };

  // Main login form UI
  const renderLoginForm = () => (
    <>
      <Grid
        container
        alignItems="center"
      >
        <Grid
          item
          xs
        >
          <Typography
            className={classes.welcomeMessage}
            gutterBottom
            variant="h4"
            style={{ color: isDarkMode ? 'white' : 'black' }}
          >
            <MarkdownRenderer markdownText={welcome_message} />
          </Typography>
          <Typography
            gutterBottom
            variant="h6"
            style={{
              fontSize: '1.0rem',
              textAlign: 'center',
              color: isDarkMode ? 'white' : 'black',
            }}
          >
            {status === 'failure'
              ? 'Please double-check the destination for your OTP.'
              : OTPLogin
              ? 'Please enter the One-Time-Password sent to your Device:'
              : 'Enter your details below:'}
          </Typography>
        </Grid>
      </Grid>
      <Grid
        style={{ width: 300, margin: 'auto' }}
        container
        direction="row"
        justifyContent="space-evenly"
        alignItems="center"
        spacing={0}
      >
        {OTPLogin || accessCodeLogin ? (
          <Grid
            item
            xs={12}
            style={{ marginBottom: 15 }}
          >
            <FormControl style={{ width: 300 }}>
              <TextField
                error={status === 'failure'}
                className="field"
                id="otp"
                label={accessCodeLogin ? 'Enter Code' : 'Enter One-Time-Password'}
                value={OTPLogin ? OTP : accessCode}
                onChange={(event) => {
                  if (OTPLogin) {
                    setOTP(event.target.value);
                  } else if (accessCodeLogin) {
                    setAccessCode(event.target.value);
                  }
                }}
                margin="normal"
                variant="outlined"
                fullWidth
                disabled={tryingLogin}
                type="text"
              />
            </FormControl>
            <Button
              variant="text"
              style={{ display: 'block', margin: 'auto' }}
              onClick={() => {
                setAccessCodeLogin(false);
                setOTPLogin(false);
              }}
            >
              <KeyboardArrowLeft style={{ marginBottom: -7, marginRight: -2 }} /> Back
            </Button>
          </Grid>
        ) : (
          <>
            <Accordion
              elevation={0}
              expanded={!showMore}
            >
              <AccordionSummary
                style={{ height: 0, display: 'none' }}
                aria-controls="emailLogin"
                id="emailLogin"
              >
                &nbsp;
              </AccordionSummary>
              <Grid
                item
                xs={12}
                style={{ marginBottom: 15 }}
              >
                <FormControl style={{ width: 300 }}>
                  <TextField
                    error={status === 'failure'}
                    className="field"
                    id="phone"
                    label="Enter Phone Number"
                    value={phone}
                    onChange={(event) => {
                      let val = event.target.value.replace(/[^0-9]/g, '');
                      if (val?.length < 11) {
                        setPhone(val);
                      }
                    }}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    disabled={tryingLogin || loginEmail}
                    type="text"
                    inputProps={{ max: 10 }}
                  />
                </FormControl>
                <Typography style={{ textAlign: 'center', margin: 'auto' }}>- OR -</Typography>
                <FormControl style={{ width: 300 }}>
                  <TextField
                    error={status === 'failure' || emailBad}
                    className="field"
                    id="email"
                    label="Enter Email"
                    value={loginEmail}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    disabled={tryingLogin || phone}
                    onChange={(event) => {
                      setEmailBad(!validateEmail(event.target.value));
                      setLoginEmail(event.target.value);
                    }}
                    type="email"
                    helperText={helperText}
                    InputProps={{ style: { color: isDarkMode ? 'white' : 'black' } }}
                    InputLabelProps={{ style: { color: isDarkMode ? 'white' : 'black' } }}
                  />
                </FormControl>
              </Grid>
            </Accordion>
            {!tryingLogin && (
              <Accordion
                elevation={0}
                expanded={showMore}
                onChange={() => setShowMore(!showMore)}
              >
                <AccordionSummary
                  aria-controls="moreWays"
                  id="moreWays"
                >
                  <Typography style={{ textAlign: 'center', margin: 'auto' }}>
                    More Login Options
                    <br />
                    {showMore ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  </Typography>
                </AccordionSummary>
                <Grid
                  item
                  xs={12}
                  style={{ marginBottom: 15, textAlign: 'left' }}
                >
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => setAccessCodeLogin(!accessCodeLogin)}
                  >
                    Login via Access Code
                  </Button>
                </Grid>
              </Accordion>
            )}
          </>
        )}
        {tryingLogin ? (
          <LinearProgress
            color="primary"
            style={{ width: '80%', display: 'block', textAlign: 'center', margin: '20px auto' }}
          />
        ) : (
          <Button
            disabled={
              !(
                (emailBad === false && loginEmail !== '') ||
                (phone?.length === 10 && phone !== '') ||
                (accessCodeLogin && accessCode?.length >= 5)
              )
            }
            variant="contained"
            style={{
              width: 300,
              marginBottom: 20,
              marginTop: 0,
              backgroundColor: tempTheme.palette.primary.main,
              color: tempTheme.palette.primary.contrastText,
            }}
            onClick={() => loginRequest()}
          >
            Sign In &nbsp;
            <LockIcon />
          </Button>
        )}
        {!tryingLogin && !OTPLogin && !accessCodeLogin && (
          <Grid
            item
            xs={12}
            style={{ marginBottom: 15 }}
          >
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    defaultChecked
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                  />
                }
                label="Remember Me?"
              />
            </FormGroup>
          </Grid>
        )}
      </Grid>
    </>
  );

  // UI for selecting from multiple partners
  const renderPartnerSelection = () => (
    <Grid
      container
      direction="column"
      alignItems="center"
    >
      <Typography
        variant="h6"
        style={{ marginBottom: 20, color: isDarkMode ? 'white' : 'black' }}
      >
        Multiple Partners Found - Please select one:
      </Typography>
      <List style={{ minWidth: 300, maxWidth: '70%', width: '100%' }}>
        {multiplePartners?.map((partner) => (
          <ListItem
            key={partner.partner_id}
            divider
            onClick={() => handleSelectPartner(partner)}
            style={{ cursor: 'pointer' }}
          >
            <ListItemAvatar>
              <img
                src={partner.partner_logo}
                alt={partner.partner_name}
                style={{ maxWidth: 80, marginRight: 10 }}
              />
            </ListItemAvatar>
            <ListItemText
              primary={partner.partner_name}
              secondary={getBetaClientHost(partner.partner_code)}
            />
            <ListItemSecondaryAction>
              <IconButton
                variant="contained"
                onClick={() => handleSelectPartner(partner)}
                style={{
                  backgroundColor: tempTheme.palette.primary.main,
                  color: tempTheme.palette.primary.contrastText,
                }}
              >
                <Login />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
      <Button
        variant="text"
        onClick={() => setMultiplePartners(null)}
        style={{ marginTop: 10 }}
      >
        Back
      </Button>
    </Grid>
  );

  // UI for selecting from multiple users
  const renderUserSelection = () => (
    <Grid
      container
      direction="column"
      alignItems="center"
    >
      <Typography
        variant="h6"
        style={{ marginBottom: 20, color: isDarkMode ? 'white' : 'black' }}
      >
        Multiple Users Found - Please select your account:
      </Typography>
      <List style={{ minWidth: 300, maxWidth: '70%', width: '100%' }}>
        {multipleUsers.map((user) => {
          const nameParts = user.name.split(' ');
          const initials = nameParts.length > 1 ? `${nameParts[0][0]}${nameParts[1][0]}` : nameParts[0][0];
          return (
            <ListItem
              key={user.user_id}
              divider
              onClick={() => handleSelectUser(user.user_id)}
              style={{ cursor: 'pointer' }}
            >
              <ListItemAvatar>
                <Avatar>{initials.toUpperCase()}</Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={user.name}
                secondary={prettyDate(user.dob) + ' | ' + ageFromDate(user.dob)}
              />
              <ListItemSecondaryAction>
                <IconButton
                  variant="contained"
                  onClick={() => handleSelectUser(user.user_id)}
                  style={{
                    backgroundColor: tempTheme.palette.primary.main,
                    color: tempTheme.palette.primary.contrastText,
                  }}
                >
                  <Launch />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
      <Button
        variant="text"
        onClick={() => setMultipleUsers(null)}
        style={{ marginTop: 10 }}
      >
        Back
      </Button>
    </Grid>
  );

  return (
    <>
      <Paper
        className={classes.paper}
        elevation={12}
        style={{ backgroundColor: finalTheme.palette.background.paper }}
      >
        <div style={{ padding: '2.5vh', textAlign: 'center' }}>
          <Logo
            height="70px"
            variant={'collab'}
          />
        </div>
        <div className={classes.container}>
          {/* If multiple partners/users returned, show selection UI */}
          {multiplePartners ? renderPartnerSelection() : multipleUsers ? renderUserSelection() : renderLoginForm()}
        </div>
      </Paper>
      <div className={classes.bgshape1}></div>
      <div className={classes.bgshape2}></div>
      <div className={classes.bgshape3}></div>
    </>
  );
};

export default LoginScreenUniversal;
